interface PhotoObject {
  original_img_url?: string
  large_img_url?: string
  medium_img_url?: string
  small_img_url?: string
}

const API_BASE_ORIGIN = (() => {
  try {
    return process.env.NEXT_PUBLIC_API_BASE_URL
      ? new URL(process.env.NEXT_PUBLIC_API_BASE_URL).origin
      : ''
  } catch {
    return ''
  }
})()

export const resolveImageUrl = (imageUrl?: string | null): string | undefined => {
  if (!imageUrl) return undefined

  if (/^https?:\/\//i.test(imageUrl)) {
    return imageUrl
  }

  if (imageUrl.startsWith('//')) {
    return `https:${imageUrl}`
  }

  if (imageUrl.startsWith('/')) {
    return API_BASE_ORIGIN ? `${API_BASE_ORIGIN}${imageUrl}` : imageUrl
  }

  return imageUrl
}

/**
 * Gets the best available image URL from a photo object by prioritizing
 * original_img_url, then large_img_url, then medium_img_url
 * 
 * @param photo - Photo object with potential image URLs
 * @returns The best available image URL or undefined
 */
export const getBestImageUrl = (photo?: PhotoObject | null): string | undefined => {
  if (!photo) return undefined
  return resolveImageUrl(
    photo.original_img_url || photo.large_img_url || photo.medium_img_url,
  )
}

/**
 * Gets optimized image URL for property details page by prioritizing
 * large_img_url (good quality, better performance), then medium_img_url, then original_img_url
 * 
 * @param photo - Photo object with potential image URLs
 * @returns The optimized image URL or undefined
 */
export const getPropertyDetailsImageUrl = (photo?: PhotoObject | null): string | undefined => {
  if (!photo) return undefined
  return resolveImageUrl(
    photo.large_img_url || photo.medium_img_url || photo.original_img_url,
  )
}

/**
 * Gets large image URL for featured/main images by prioritizing
 * large_img_url, then original_img_url, then medium_img_url
 * 
 * @param photo - Photo object with potential image URLs
 * @returns The large image URL or undefined
 */
export const getLargeImageUrl = (photo?: PhotoObject | null): string | undefined => {
  if (!photo) return undefined
  return resolveImageUrl(
    photo.large_img_url || photo.original_img_url || photo.medium_img_url,
  )
}

/**
 * Gets medium image URL for thumbnails/small images by prioritizing
 * medium_img_url, then small_img_url, then large_img_url
 * 
 * @param photo - Photo object with potential image URLs
 * @returns The medium image URL or undefined
 */
export const getMediumImageUrl = (photo?: PhotoObject | null): string | undefined => {
  if (!photo) return undefined
  return resolveImageUrl(
    photo.medium_img_url || photo.small_img_url || photo.large_img_url,
  )
}
